// src/pages/InspeccionEntrada.jsx
// ═══════════════════════════════════════════════════════════════════════════════
// 📥 INSPECCIÓN DE ENTRADA - SISTEMA RENTACAR eFIT
// Registro del estado del vehículo al momento de devolución por el cliente
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import api from "../services/api";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const descargarContratoPDF = async (rentaId, numero_contrato) => {
  try {
    const res = await api.get(`/rencar/rentas/${rentaId}/contrato`, { responseType: 'blob' });
    const url  = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `contrato-${numero_contrato || 'RC-' + String(rentaId).padStart(4, '0')}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo descargar el contrato PDF', confirmButtonColor: '#3b82f6' });
  }
};

const InspeccionEntrada = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [renta, setRenta] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [finalizando, setFinalizando] = useState(false);

  const [form, setForm] = useState({
    gasolina: "",
    kilometraje: "",
    rayaduras: "",
    llantas: "excelente",
    fecha_retorno_real: new Date().toISOString().split('T')[0],
    accesorios: {
      gato: true,
      llave_ruedas: true,
      triangulo: true,
      botiquin: false,
      extintor: false,
    },
    // Cargos adicionales
    cargo_combustible: 0,
    cargo_km_extra: 0,
    cargo_danos: 0,
    // Depósito
    deposito_opcion: "devolver", // devolver | aplicar | parcial
    deposito_aplicado: 0,
    deposito_devuelto: 0,
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔄 CARGAR DATOS DE LA RENTA
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    const cargarDatos = async () => {
      if (!id) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "ID de renta inválido",
          confirmButtonColor: "#3b82f6"
        });
        navigate("/rencar");
        return;
      }

      try {
        setCargando(true);
        const res = await api.get(`/rencar/rentas/${id}`);
        
        if (res.data.ok && res.data.renta) {
          const rentaData = res.data.renta;
          setRenta(rentaData);
          
          // Verificar estado - solo permitir si está en_uso
          if (rentaData.estado !== "en_uso") {
            Swal.fire({
              icon: "warning",
              title: "Renta no disponible",
              html: `
                <p>Esta renta está en estado: <strong>${rentaData.estado}</strong></p>
                <p>Solo se pueden recibir vehículos de rentas en estado "en_uso"</p>
              `,
              confirmButtonColor: "#3b82f6"
            }).then(() => navigate("/rencar"));
          }

          // Si hay inspección de salida, pre-llenar datos
          if (rentaData.inspecciones?.length > 0) {
            const inspeccionSalida = rentaData.inspecciones.find(i => i.tipo === 'salida');
            if (inspeccionSalida) {
              setForm(prev => ({
                ...prev,
                kilometraje: inspeccionSalida.kilometraje || ""
              }));
            }
          }
        }
      } catch (error) {
        console.error("❌ Error cargando renta:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudo cargar la información de la renta",
          confirmButtonColor: "#3b82f6"
        });
        navigate("/rencar");
      } finally {
        setCargando(false);
      }
    };

    cargarDatos();
  }, [id, navigate]);

  // ═══════════════════════════════════════════════════════════════════════════
  // 📊 CALCULAR DIFERENCIAS
  // ═══════════════════════════════════════════════════════════════════════════
  const inspeccionSalida = renta?.inspecciones?.find(i => i.tipo === 'salida');
  const kilometrajeInicial = inspeccionSalida?.kilometraje || 0;

  const kilometrajeRecorrido = (form.kilometraje && kilometrajeInicial)
    ? (parseFloat(form.kilometraje) - parseFloat(kilometrajeInicial))
    : 0;

  const calcularDiasReales = () => {
    if (!renta || !form.fecha_retorno_real) return 0;
    const salida  = new Date(renta.fecha_salida);
    const retorno = new Date(form.fecha_retorno_real);
    const diff    = retorno.getTime() - salida.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const diasReales     = calcularDiasReales();
  const diasProgramados = renta?.dias || 0;
  const diasExtra       = Math.max(0, diasReales - diasProgramados);

  const costoDiario = parseFloat(renta?.Vehiculo?.costo_diario || 0);
  const tarifaBase  = diasReales * costoDiario;
  const cargoDiasExtra = diasExtra * costoDiario;

  // Totales calculados en tiempo real
  const totalCargos = parseFloat(form.cargo_combustible || 0) +
                      parseFloat(form.cargo_km_extra    || 0) +
                      parseFloat(form.cargo_danos       || 0) +
                      cargoDiasExtra;

  const depositoOriginal = parseFloat(renta?.deposito || 0);

  // Calcular deposito_aplicado y deposito_devuelto según opción
  const calcDeposito = () => {
    if (form.deposito_opcion === "aplicar") {
      return { aplicado: depositoOriginal, devuelto: 0 };
    } else if (form.deposito_opcion === "devolver") {
      return { aplicado: 0, devuelto: depositoOriginal };
    } else {
      const aplicado = Math.min(parseFloat(form.deposito_aplicado || 0), depositoOriginal);
      return { aplicado, devuelto: depositoOriginal - aplicado };
    }
  };

  const { aplicado: depAplicado, devuelto: depDevuelto } = calcDeposito();
  const totalFinal = Math.max(0, tarifaBase + totalCargos - depAplicado);

  // ═══════════════════════════════════════════════════════════════════════════
  // 💾 FINALIZAR RENTA
  // ═══════════════════════════════════════════════════════════════════════════
  const finalizarRenta = async () => {
    // Validaciones
    if (!form.gasolina || !form.kilometraje) {
      return Swal.fire({
        icon: "warning",
        title: "Campos incompletos",
        text: "Nivel de gasolina y kilometraje son obligatorios",
        confirmButtonColor: "#3b82f6"
      });
    }

    if (parseFloat(form.kilometraje) < kilometrajeInicial) {
      return Swal.fire({
        icon: "error",
        title: "Kilometraje inválido",
        text: `El kilometraje no puede ser menor al inicial (${kilometrajeInicial} km)`,
        confirmButtonColor: "#3b82f6"
      });
    }

    // Confirmación
    const fmt = (n) => `USD $${parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
    const confirm = await Swal.fire({
      title: "¿Finalizar Renta?",
      html: `
        <div style="text-align: left; padding: 10px; font-size: 14px;">
          <p><strong>Resumen de Devolución:</strong></p>
          <table style="width:100%; border-collapse:collapse;">
            <tr><td>Kilometraje recorrido</td><td style="text-align:right"><strong>${kilometrajeRecorrido} km</strong></td></tr>
            <tr><td>Días reales</td><td style="text-align:right"><strong>${diasReales} días</strong></td></tr>
            ${diasExtra > 0 ? `<tr><td style="color:#f59e0b">⚠️ Días extra</td><td style="text-align:right;color:#f59e0b">${diasExtra} días (${fmt(cargoDiasExtra)})</td></tr>` : ''}
            <tr><td>Tarifa base</td><td style="text-align:right">${fmt(tarifaBase)}</td></tr>
            ${parseFloat(form.cargo_combustible) > 0 ? `<tr><td style="color:#dc2626">Cargo combustible</td><td style="text-align:right;color:#dc2626">${fmt(form.cargo_combustible)}</td></tr>` : ''}
            ${parseFloat(form.cargo_km_extra) > 0 ? `<tr><td style="color:#dc2626">Cargo km extra</td><td style="text-align:right;color:#dc2626">${fmt(form.cargo_km_extra)}</td></tr>` : ''}
            ${parseFloat(form.cargo_danos) > 0 ? `<tr><td style="color:#dc2626">Cargo daños</td><td style="text-align:right;color:#dc2626">${fmt(form.cargo_danos)}</td></tr>` : ''}
            ${depAplicado > 0 ? `<tr><td style="color:#16a34a">Depósito aplicado</td><td style="text-align:right;color:#16a34a">−${fmt(depAplicado)}</td></tr>` : ''}
            <tr style="border-top:2px solid #e2e8f0;font-size:16px;"><td><strong>TOTAL A PAGAR</strong></td><td style="text-align:right"><strong>${fmt(totalFinal)}</strong></td></tr>
            ${depDevuelto > 0 ? `<tr><td style="color:#16a34a">Depósito a devolver</td><td style="text-align:right;color:#16a34a">${fmt(depDevuelto)}</td></tr>` : ''}
          </table>
          <p style="margin-top:8px">Gasolina: <strong>${form.gasolina}</strong></p>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, Finalizar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#10b981",
      cancelButtonColor: "#64748b"
    });

    if (!confirm.isConfirmed) return;

    setFinalizando(true);
    try {
      const resCerrar = await api.put(`/rencar/rentas/cerrar/${id}`, {
        fecha_retorno_real:  form.fecha_retorno_real,
        observaciones:       form.rayaduras || "Sin observaciones",
        gasolina_entrada:    form.gasolina,
        kilometraje_entrada: parseFloat(form.kilometraje),
        cargo_combustible:   parseFloat(form.cargo_combustible  || 0),
        cargo_km_extra:      parseFloat(form.cargo_km_extra     || 0),
        cargo_danos:         parseFloat(form.cargo_danos        || 0),
        deposito_aplicado:   depAplicado,
        deposito_devuelto:   depDevuelto,
      });

      if (resCerrar.data.ok) {
        const r = resCerrar.data.resumen || {};
        Swal.fire({
          icon: "success",
          title: "✅ Renta Finalizada",
          html: `
            <div style="text-align: left; padding: 10px; font-size:14px;">
              <p><strong>Vehículo devuelto exitosamente</strong></p>
              <p>Total contrato: <span style="color:#10b981;font-weight:bold;font-size:18px;">USD $${parseFloat(r.total_pagar || 0).toLocaleString('en-US',{minimumFractionDigits:2})}</span></p>
              ${r.total_pagar_dop ? `<p style="color:#6366f1;font-size:13px;">≈ RD$ ${parseFloat(r.total_pagar_dop).toLocaleString('es-DO',{minimumFractionDigits:2})} (tasa ${parseFloat(r.tasa_cambio||0).toFixed(2)})</p>` : ''}
              ${r.deposito_devuelto > 0 ? `<p style="color:#16a34a">Depósito a devolver al cliente: USD $${parseFloat(r.deposito_devuelto).toLocaleString('en-US',{minimumFractionDigits:2})}</p>` : ''}
              <hr style="margin:10px 0"/>
              <p>¿Deseas descargar el contrato?</p>
            </div>
          `,
          showDenyButton: true,
          confirmButtonText: "📄 Descargar Contrato",
          denyButtonText: "Ir al Dashboard",
          confirmButtonColor: "#3b82f6",
          denyButtonColor: "#64748b",
          allowOutsideClick: false
        }).then((result) => {
          if (result.isConfirmed) {
            descargarContratoPDF(id, renta?.numero_contrato);
            setTimeout(() => navigate("/rencar"), 1500);
          } else {
            navigate("/rencar");
          }
        });
      }
    } catch (error) {
      console.error("❌ Error finalizando renta:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.msg || "No se pudo finalizar la renta",
        confirmButtonColor: "#3b82f6"
      });
    } finally {
      setFinalizando(false);
    }
  };

  if (cargando) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Cargando datos de devolución...</p>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 🎨 RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div style={styles.container}>
      {/* Page Header */}
      <div style={styles.pageHeader}>
        <div style={styles.pageTitle}>
          <span style={styles.pageTitleIcon}>📥</span>
          <div>
            <h1 style={styles.pageTitleText}>Inspección de Entrada</h1>
            <p style={styles.pageTitleSub}>
              Renta #{id} • {renta?.Vehiculo?.marca} {renta?.Vehiculo?.modelo} 
              <span style={styles.placaBadge}>{renta?.Vehiculo?.placa}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Info Card */}
      <div style={styles.infoCard}>
        <div style={styles.infoRow}>
          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>Cliente:</span>
            <span style={styles.infoValue}>{renta?.Cliente?.nombre}</span>
          </div>
          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>Salida:</span>
            <span style={styles.infoValue}>
              {new Date(renta?.fecha_salida).toLocaleDateString("es-DO")}
            </span>
          </div>
          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>Retorno Programado:</span>
            <span style={styles.infoValue}>
              {new Date(renta?.fecha_retorno_programada).toLocaleDateString("es-DO")}
            </span>
          </div>
          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>Km Inicial:</span>
            <span style={styles.infoValue}>
              {kilometrajeInicial > 0 ? `${kilometrajeInicial} km` : '⚠️ No registrado'}
            </span>
          </div>
        </div>
        
        {/* Advertencia si no hay inspección de salida */}
        {!inspeccionSalida && (
          <div style={styles.warningBanner}>
            ⚠️ Esta renta no tiene inspección de salida registrada. 
            El kilometraje recorrido no podrá calcularse automáticamente.
          </div>
        )}
      </div>

      {/* Formulario */}
      <div style={styles.formCard}>
        {/* Sección 1: Fecha Real */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>📅 Fecha de Devolución</h3>
          <div style={styles.formGrid}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Fecha Real de Retorno *</label>
              <input
                type="date"
                style={styles.input}
                value={form.fecha_retorno_real}
                onChange={(e) => setForm({ ...form, fecha_retorno_real: e.target.value })}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
            
            {diasReales > 0 && (
              <div style={diasExtra > 0 ? styles.warningCard : styles.successCard}>
                <div style={styles.cardLabel}>Duración Real</div>
                <div style={styles.cardValue}>{diasReales} días</div>
                {diasExtra !== 0 && (
                  <div style={styles.cardHint}>
                    {diasExtra > 0 ? `+${diasExtra} días extra` : `${Math.abs(diasExtra)} días antes`}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sección 2: Estado del Vehículo */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>📊 Estado al Recibir</h3>
          <div style={styles.formGrid}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Kilometraje Actual *</label>
              <input
                type="number"
                style={styles.input}
                placeholder={`Inicial: ${kilometrajeInicial} km`}
                value={form.kilometraje}
                onChange={(e) => setForm({ ...form, kilometraje: e.target.value })}
                min={kilometrajeInicial}
                step="1"
              />
              {kilometrajeRecorrido > 0 && (
                <span style={{...styles.inputHint, color: '#3b82f6', fontWeight: '600'}}>
                  ✓ Recorrido: {kilometrajeRecorrido} km
                </span>
              )}
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Nivel de Gasolina *</label>
              <select
                style={styles.input}
                value={form.gasolina}
                onChange={(e) => setForm({ ...form, gasolina: e.target.value })}
              >
                <option value="">Seleccione...</option>
                <option value="lleno">🟢 Lleno (4/4)</option>
                <option value="3/4">🟡 Tres Cuartos (3/4)</option>
                <option value="1/2">🟠 Medio (1/2)</option>
                <option value="1/4">🔴 Un Cuarto (1/4)</option>
                <option value="vacio">⚫ Vacío</option>
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Estado de Llantas</label>
              <select
                style={styles.input}
                value={form.llantas}
                onChange={(e) => setForm({ ...form, llantas: e.target.value })}
              >
                <option value="excelente">✅ Excelente</option>
                <option value="buenas">👍 Buenas</option>
                <option value="regulares">⚠️ Regulares</option>
                <option value="necesita_cambio">❌ Necesita Cambio</option>
              </select>
            </div>
          </div>
        </div>

        {/* Sección 3: Accesorios */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>🔧 Accesorios Devueltos</h3>
          <div style={styles.checklistGrid}>
            {Object.entries(form.accesorios).map(([key, value]) => (
              <label key={key} style={styles.checkItem}>
                <input
                  type="checkbox"
                  checked={value}
                  onChange={() => setForm({
                    ...form,
                    accesorios: { ...form.accesorios, [key]: !value }
                  })}
                  style={styles.checkbox}
                />
                <span>{key.replace("_", " ").toUpperCase()}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Sección 4: Observaciones */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>📝 Daños o Novedades</h3>
          <textarea
            style={styles.textarea}
            value={form.rayaduras}
            onChange={(e) => setForm({ ...form, rayaduras: e.target.value })}
            placeholder="Reporte nuevos rayones, abolladuras, piezas faltantes o cualquier novedad..."
            rows="4"
          />
        </div>

        {/* Sección 5: Cargos Adicionales */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>💸 Cargos Adicionales</h3>
          <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
            Ingrese montos adicionales a cobrar según el estado del vehículo al recibir.
          </p>
          <div style={styles.formGrid}>
            {/* Combustible */}
            <div style={styles.formGroup}>
              <label style={styles.label}>
                ⛽ Cargo por Combustible Faltante
                {inspeccionSalida?.gasolina && (
                  <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: '6px' }}>
                    (salió: {inspeccionSalida.gasolina} / retorna: {form.gasolina || '?'})
                  </span>
                )}
              </label>
              <input
                type="number"
                style={styles.input}
                placeholder="USD $0.00"
                value={form.cargo_combustible || ''}
                onChange={(e) => setForm({ ...form, cargo_combustible: e.target.value })}
                min="0"
                step="0.01"
              />
            </div>

            {/* Km extra */}
            <div style={styles.formGroup}>
              <label style={styles.label}>
                🛣️ Cargo por Km Excedidos
                {kilometrajeRecorrido > 0 && (
                  <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: '6px' }}>
                    ({kilometrajeRecorrido} km recorridos)
                  </span>
                )}
              </label>
              <input
                type="number"
                style={styles.input}
                placeholder="USD $0.00"
                value={form.cargo_km_extra || ''}
                onChange={(e) => setForm({ ...form, cargo_km_extra: e.target.value })}
                min="0"
                step="0.01"
              />
            </div>

            {/* Daños */}
            <div style={styles.formGroup}>
              <label style={styles.label}>🔧 Cargo por Daños Nuevos</label>
              <input
                type="number"
                style={styles.input}
                placeholder="USD $0.00"
                value={form.cargo_danos || ''}
                onChange={(e) => setForm({ ...form, cargo_danos: e.target.value })}
                min="0"
                step="0.01"
              />
            </div>

            {/* Días extra (automático) */}
            {diasExtra > 0 && (
              <div style={{ ...styles.warningCard, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={styles.cardLabel}>⏰ Días Extra Calculados</div>
                <div style={styles.cardValue}>{diasExtra} días</div>
                <div style={{ ...styles.cardHint, color: '#92400e' }}>
                  USD ${cargoDiasExtra.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  <br /><small>({diasExtra} × USD ${costoDiario.toLocaleString('en-US', { minimumFractionDigits: 2 })}/día)</small>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sección 6: Depósito de Garantía */}
        {depositoOriginal > 0 && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>🏦 Depósito de Garantía</h3>
            <div style={{ ...styles.infoCard, background: '#f0fdf4', border: '2px solid #86efac', marginBottom: '16px', padding: '14px 20px' }}>
              <strong>Depósito recibido al inicio:</strong>{' '}
              <span style={{ color: '#16a34a', fontWeight: 800, fontSize: '16px' }}>
                USD ${depositoOriginal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
              {[
                { val: 'devolver', label: '↩️ Devolver todo al cliente' },
                { val: 'aplicar',  label: '✅ Aplicar todo al saldo' },
                { val: 'parcial',  label: '⚖️ Aplicar parcialmente' },
              ].map(opt => (
                <button
                  key={opt.val}
                  style={{
                    padding: '10px 18px',
                    borderRadius: '12px',
                    border: '2px solid',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    background: form.deposito_opcion === opt.val ? '#16a34a' : 'white',
                    color:  form.deposito_opcion === opt.val ? 'white' : '#475569',
                    borderColor: form.deposito_opcion === opt.val ? '#16a34a' : '#e2e8f0',
                  }}
                  onClick={() => setForm({ ...form, deposito_opcion: opt.val, deposito_aplicado: 0 })}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {form.deposito_opcion === 'parcial' && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Monto a aplicar al saldo (USD)</label>
                <input
                  type="number"
                  style={styles.input}
                  placeholder={`Máximo: USD $${depositoOriginal.toLocaleString()}`}
                  value={form.deposito_aplicado || ''}
                  onChange={(e) => setForm({ ...form, deposito_aplicado: Math.min(parseFloat(e.target.value || 0), depositoOriginal) })}
                  min="0"
                  max={depositoOriginal}
                  step="0.01"
                />
                <span style={{ ...styles.inputHint, color: '#16a34a', fontWeight: '600' }}>
                  A devolver al cliente: USD ${(depositoOriginal - parseFloat(form.deposito_aplicado || 0)).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Resumen Final */}
        <div style={{ background: '#f8fafc', borderRadius: '16px', padding: '24px', border: '2px solid #e2e8f0', marginBottom: '24px' }}>
          <h3 style={{ ...styles.sectionTitle, marginBottom: '16px' }}>🧾 Resumen Final</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748b' }}>Tarifa base ({diasReales} días × USD ${costoDiario.toLocaleString('en-US', { minimumFractionDigits: 2 })})</span>
              <strong>USD ${tarifaBase.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
            </div>
            {diasExtra > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#dc2626' }}>
                <span>Días extra ({diasExtra} días)</span>
                <strong>+ USD ${cargoDiasExtra.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
              </div>
            )}
            {parseFloat(form.cargo_combustible) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#dc2626' }}>
                <span>Cargo combustible</span>
                <strong>+ USD ${parseFloat(form.cargo_combustible).toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
              </div>
            )}
            {parseFloat(form.cargo_km_extra) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#dc2626' }}>
                <span>Cargo km excedidos</span>
                <strong>+ USD ${parseFloat(form.cargo_km_extra).toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
              </div>
            )}
            {parseFloat(form.cargo_danos) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#dc2626' }}>
                <span>Cargo daños</span>
                <strong>+ USD ${parseFloat(form.cargo_danos).toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
              </div>
            )}
            {depAplicado > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#16a34a' }}>
                <span>Depósito aplicado</span>
                <strong>− USD ${depAplicado.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '18px', fontWeight: 800, borderTop: '2px solid #e2e8f0', paddingTop: '12px', marginTop: '4px' }}>
              <span>TOTAL A PAGAR</span>
              <span style={{ color: '#10b981' }}>USD ${totalFinal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
            </div>
            {depDevuelto > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#16a34a', fontSize: '13px' }}>
                <span>Depósito a devolver al cliente</span>
                <strong>USD ${depDevuelto.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
              </div>
            )}
          </div>
        </div>

        {/* Botones de Acción */}
        <div style={styles.formActions}>
          <button
            style={styles.btnSecondary}
            onClick={() => navigate("/rencar")}
            disabled={finalizando}
          >
            ← Cancelar
          </button>
          <button
            style={{ ...styles.btnSecondary, borderColor: '#3b82f6', color: '#3b82f6' }}
            onClick={() => descargarContratoPDF(id, renta?.numero_contrato)}
            title="Descargar contrato antes de cerrar"
          >
            📄 Ver Contrato
          </button>
          <button
            style={styles.btnPrimary}
            onClick={finalizarRenta}
            disabled={finalizando || !form.gasolina || !form.kilometraje}
          >
            {finalizando ? (
              <div style={styles.btnLoading}>
                <div style={styles.btnSpinner}></div>
                <span>Finalizando...</span>
              </div>
            ) : (
              <>
                <span style={styles.btnIcon}>✓</span>
                <span>Finalizar y Cerrar Renta</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Animaciones */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 🎨 ESTILOS eFIT
// ═══════════════════════════════════════════════════════════════════════════════
const styles = {
  container: {
    minHeight: "calc(100vh - 70px)",
    background: "linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)",
    padding: "24px 32px",
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"
  },

  // Page Header
  pageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px"
  },
  pageTitle: {
    display: "flex",
    alignItems: "center",
    gap: "16px"
  },
  pageTitleIcon: {
    fontSize: "36px",
    background: "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)",
    padding: "14px",
    borderRadius: "16px",
    border: "2px solid #6ee7b7"
  },
  pageTitleText: {
    fontSize: "28px",
    fontWeight: "800",
    color: "#1e293b",
    margin: 0
  },
  pageTitleSub: {
    fontSize: "14px",
    color: "#64748b",
    margin: "4px 0 0 0",
    display: "flex",
    alignItems: "center",
    gap: "8px"
  },
  placaBadge: {
    background: "#1e293b",
    color: "white",
    padding: "2px 10px",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: "700",
    marginLeft: "8px"
  },

  // Info Card
  infoCard: {
    background: "white",
    borderRadius: "16px",
    padding: "20px",
    marginBottom: "24px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    border: "1px solid #e2e8f0"
  },
  infoRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "20px"
  },
  infoItem: {
    display: "flex",
    flexDirection: "column",
    gap: "4px"
  },
  infoLabel: {
    fontSize: "12px",
    color: "#64748b",
    fontWeight: "600",
    textTransform: "uppercase"
  },
  infoValue: {
    fontSize: "16px",
    color: "#1e293b",
    fontWeight: "700"
  },
  warningBanner: {
    marginTop: "16px",
    padding: "12px 16px",
    background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
    border: "2px solid #fcd34d",
    borderRadius: "12px",
    color: "#92400e",
    fontSize: "14px",
    fontWeight: "600",
    textAlign: "center"
  },

  // Form Card
  formCard: {
    background: "white",
    borderRadius: "20px",
    padding: "32px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    border: "1px solid #e2e8f0",
    maxWidth: "1000px",
    margin: "0 auto"
  },

  // Sections
  section: {
    marginBottom: "32px",
    paddingBottom: "24px",
    borderBottom: "1px solid #f1f5f9"
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#1e293b",
    marginBottom: "20px"
  },

  // Form Elements
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "20px"
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  },
  label: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#475569"
  },
  input: {
    padding: "12px 16px",
    fontSize: "15px",
    border: "2px solid #e2e8f0",
    borderRadius: "12px",
    outline: "none",
    background: "#f8fafc",
    transition: "all 0.2s",
    fontFamily: "inherit"
  },
  textarea: {
    padding: "12px 16px",
    fontSize: "15px",
    border: "2px solid #e2e8f0",
    borderRadius: "12px",
    outline: "none",
    background: "#f8fafc",
    resize: "vertical",
    fontFamily: "inherit"
  },
  inputHint: {
    fontSize: "12px",
    color: "#94a3b8",
    fontStyle: "italic"
  },

  // Warning/Success Cards
  warningCard: {
    padding: "16px",
    background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
    borderRadius: "12px",
    border: "2px solid #fcd34d",
    textAlign: "center"
  },
  successCard: {
    padding: "16px",
    background: "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)",
    borderRadius: "12px",
    border: "2px solid #6ee7b7",
    textAlign: "center"
  },
  cardLabel: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#475569",
    marginBottom: "4px"
  },
  cardValue: {
    fontSize: "24px",
    fontWeight: "800",
    color: "#1e293b"
  },
  cardHint: {
    fontSize: "13px",
    fontWeight: "600",
    marginTop: "4px"
  },

  // Checklist
  checklistGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "12px",
    background: "#f8fafc",
    padding: "16px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0"
  },
  checkItem: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    padding: "4px"
  },
  checkbox: {
    width: "18px",
    height: "18px",
    cursor: "pointer"
  },

  // Buttons
  formActions: {
    display: "flex",
    gap: "16px",
    justifyContent: "flex-end",
    marginTop: "32px",
    paddingTop: "24px",
    borderTop: "2px solid #f1f5f9"
  },
  btnPrimary: {
    padding: "14px 32px",
    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    color: "white",
    border: "none",
    borderRadius: "12px",
    fontSize: "16px",
    fontWeight: "700",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(16, 185, 129, 0.4)",
    display: "flex",
    alignItems: "center",
    gap: "8px"
  },
  btnSecondary: {
    padding: "14px 32px",
    background: "#f1f5f9",
    color: "#475569",
    border: "2px solid #e2e8f0",
    borderRadius: "12px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer"
  },
  btnIcon: {
    fontSize: "18px"
  },
  btnLoading: {
    display: "flex",
    alignItems: "center",
    gap: "12px"
  },
  btnSpinner: {
    width: "16px",
    height: "16px",
    border: "2px solid rgba(255, 255, 255, 0.3)",
    borderTopColor: "white",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite"
  },

  // Loading
  loadingContainer: {
    minHeight: "80vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center"
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "4px solid #e2e8f0",
    borderTopColor: "#10b981",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite"
  },
  loadingText: {
    marginTop: "16px",
    color: "#64748b",
    fontSize: "16px",
    fontWeight: "600"
  }
};

export default InspeccionEntrada;